import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WeekView } from '@devexpress/dx-react-scheduler-material-ui';
import {
  isAfter,
  isEqual,
  isBefore,
  getHours,
  getMinutes,
  endOfDay,
} from 'date-fns';
import useWorkScheduleCalendarContext from 'components/AddWorkSchedule/WorkScheduleCalendar/context/useWorkScheduleCalendarContext';
import { draftWorkScheduleId } from 'components/AddWorkSchedule/WorkScheduleCalendar/context/WorkScheduleCalendarProvider';
import extractTimeFromDate from 'helpers/extractTimeFromDate';
import { wholeDayInSeconds, millisecondsInSecond } from 'helpers/timeConsts';
import getDayValues from 'components/SchedulePreviewCalendar/libComponents/DayScaleCellComponent/getDayValues';
import TimeTableHoverComponent from '../TimeTableHoverComponent';
import StyledTimeTableCell from './TimeTableCell.styled';

function TimeTableCellComponent({
  startDate,
  endDate,
  ...restProps
}: WeekView.TimeTableCellProps) {
  const isLastTile =
    getHours(endDate as Date) === 23 && getMinutes(endDate as Date) === 59;

  const convertedEndDate = useMemo(
    () => (isLastTile ? endOfDay(endDate as Date) : endDate),
    [endDate, isLastTile],
  );
  const { t } = useTranslation();
  const {
    handleAddDraftWorkSchedule,
    setIsDraftCreating,
    updateEndDateOfDraftWorkSchedule,
    isDraftCreating,
    workSchedules,
    setDraftAnchorEl,
    schedule,
  } = useWorkScheduleCalendarContext();

  const { dayWithWeekOffset } = useMemo(
    () =>
      getDayValues({
        startDate: startDate as Date,
        isRegularWeek: schedule.regular,
      }),
    [startDate, schedule.regular],
  );

  const addDraftSchedule = (event: {
    currentTarget: React.SetStateAction<HTMLDivElement | null>;
  }) => {
    if (!startDate || !endDate) {
      return;
    }

    const timeTo = isLastTile
      ? wholeDayInSeconds
      : extractTimeFromDate(endDate) / millisecondsInSecond;
    const timeFrom = extractTimeFromDate(startDate) / millisecondsInSecond;

    setIsDraftCreating(true);
    handleAddDraftWorkSchedule({
      startDate,
      endDate: convertedEndDate,
      timeTo,
      timeFrom,
      day: dayWithWeekOffset,
    });
    setDraftAnchorEl(event.currentTarget);
  };

  const isDraftInWorkSchedule = useMemo(
    () =>
      workSchedules.some(
        (workSchedule) => workSchedule.id === draftWorkScheduleId,
      ),
    [workSchedules],
  );

  const isWorkScheduleOverlapping = useMemo(
    () =>
      workSchedules.some((workSchedule) => {
        if (
          !startDate ||
          !endDate ||
          !(workSchedule.startDate instanceof Date) ||
          !(workSchedule.endDate instanceof Date)
        ) {
          return false;
        }

        return (
          ((isAfter(workSchedule.endDate, endDate) ||
            isEqual(workSchedule.endDate, endDate)) &&
            isBefore(workSchedule.startDate, startDate)) ||
          isEqual(workSchedule.startDate, startDate)
        );
      }),
    [workSchedules, startDate, endDate],
  );

  const onMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const target = event.target as HTMLDivElement;
    if (!isDraftCreating || !target.dataset.end) {
      return;
    }
    updateEndDateOfDraftWorkSchedule(new Date(target.dataset.end));
  };

  const onMouseUp = () => {
    if (!isDraftCreating) {
      return;
    }
    setIsDraftCreating(false);
  };

  return (
    <StyledTimeTableCell
      data-end={convertedEndDate}
      data-start={startDate}
      {...restProps}
      style={{
        height: '5rem',
      }}
      isDraftInWorkSchedule={isDraftInWorkSchedule}
      isWorkScheduleOverlapping={isWorkScheduleOverlapping}
      onMouseDown={addDraftSchedule}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {!isDraftInWorkSchedule && (
        <TimeTableHoverComponent label={t('project_tracking.work')} />
      )}
    </StyledTimeTableCell>
  );
}

export default memo(TimeTableCellComponent);
